import { computed, inject, Injectable, signal } from '@angular/core';
import { select } from '@ngxs/store';
import { MatterWordDocumentWithMetadata } from '@state/matter/matter-word-documents/matter-word-documents.selectors';
import { UserPreferencesDispatch } from '@state/user-preferences/user-preferences.dispatch';
import { UserPreferencesState } from '@state/user-preferences/user-preferences.state';
import Fuse from 'fuse.js';

export interface MyDocument extends MatterWordDocumentWithMetadata {
	isNew: boolean;
	isEditing?: boolean;
}

export interface WordDocumentsState {
	myDocuments: MyDocument[];
	filteredDocuments: MyDocument[];
}

export type SortDirection = 'asc' | 'desc' | null;
export type SortField = 'title' | 'lastUpdated' | 'packages' | null;

const WORD_DOCUMENTS_TABLE_ID = 'word-documents-table';

@Injectable()
export class WordDocumentsViewModel {
	private readonly userPreferencesDispatch = inject(UserPreferencesDispatch);
	private readonly tableSortPreferences = select(
		UserPreferencesState.getTableSortPreferences,
	);

	constructor() {
		this.initializeSortPreferences();
	}

	private initializeSortPreferences(): void {
		const preferences = this.tableSortPreferences()(WORD_DOCUMENTS_TABLE_ID);
		this._sortField.set((preferences?.field as SortField) || null);
		this._sortDirection.set(preferences?.direction || null);
	}

	private readonly _documents = signal<MyDocument[]>([]);
	private readonly _searchQuery = signal<string>('');
	private readonly _sortField = signal<SortField>(null);
	private readonly _sortDirection = signal<SortDirection>(null);
	private fuse: Fuse<MyDocument>;

	readonly documents = this._documents.asReadonly();
	readonly searchQuery = this._searchQuery.asReadonly();
	readonly sortField = this._sortField.asReadonly();
	readonly sortDirection = this._sortDirection.asReadonly();

	readonly sortDirections = computed(() => ({
		title: this._sortField() === 'title' ? this._sortDirection() : null,
		lastUpdated:
			this._sortField() === 'lastUpdated' ? this._sortDirection() : null,
		packages: this._sortField() === 'packages' ? this._sortDirection() : null,
	}));

	readonly sortIcons = computed(() => ({
		title: this.getSortIconForField('title'),
		lastUpdated: this.getSortIconForField('lastUpdated'),
		packages: this.getSortIconForField('packages'),
	}));

	readonly filteredDocuments = computed(() => {
		let documents = this._documents().filter(
			(x) => !x.document.isAMergedDocument,
		);
		const query = this._searchQuery();

		if (query && query.trim() !== '') {
			const searchResults = this.fuse.search(query);
			documents = searchResults.map((result) => result.item);
		}

		const sortField = this._sortField();
		const sortDirection = this._sortDirection();

		if (sortField && sortDirection) {
			documents = [...documents].sort((a, b) => {
				let comparison = 0;
				let dateA: number;
				let dateB: number;

				switch (sortField) {
					case 'title':
						comparison = a.document.title.localeCompare(b.document.title);
						break;
					case 'lastUpdated':
						dateA = new Date(
							a.document.updatedAt || a.document.createdAt,
						).getTime();
						dateB = new Date(
							b.document.updatedAt || b.document.createdAt,
						).getTime();
						comparison = dateB - dateA;
						break;
					case 'packages':
						comparison =
							(a.includedInPackages?.length || 0) -
							(b.includedInPackages?.length || 0);
						break;
				}

				return sortDirection === 'asc' ? comparison : -comparison;
			});
		}

		return documents;
	});

	private initializeFuse(documents: MyDocument[]): void {
		this.fuse = new Fuse(documents, {
			keys: [
				{
					name: 'document.title',
					weight: 1,
				},
			],
			threshold: 0.2,
			ignoreLocation: true,
			includeMatches: true,
		});
	}

	updateSearchQuery(query: string): void {
		this._searchQuery.set(query);
	}

	updateDocuments(documents: MyDocument[]): void {
		this._documents.set(documents);
		this.initializeFuse(documents);
	}

	toggleSort(field: SortField): void {
		let direction: SortDirection = 'asc';

		if (this._sortField() === field) {
			if (this._sortDirection() === 'asc') {
				direction = 'desc';
			} else if (this._sortDirection() === 'desc') {
				field = null;
				direction = null;
			}
		}

		this._sortField.set(field);
		this._sortDirection.set(direction);

		this.userPreferencesDispatch.SetTableSortPreferences(
			WORD_DOCUMENTS_TABLE_ID,
			{
				field,
				direction,
			},
		);
	}

	private getSortIconForField(field: SortField): string {
		const direction = this.sortDirections()[field];
		if (!direction) return 'selector';
		return direction === 'asc' ? 'chevron-up' : 'chevron-down';
	}
}
